import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Breadcrumbs, Link, CircularProgress,
    Paper, IconButton, Tooltip, Avatar, Fade, Container, Grid,
    Card, CardContent, Chip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import SchoolIcon from '@mui/icons-material/School';
import LayersIcon from '@mui/icons-material/Layers';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import api from '../services/api';
import { Category, Course, Section } from '../types/course';
import CourseModal from '../components/course/modals/CourseModal';
import SectionModal from '../components/course/modals/SectionModal';
import CategoryModal from '../components/course/modals/CategoryModal';
import { designTokens } from '../theme/designTokens';
import { useToast } from '../context/ToastContext';

const CategoryDetails = () => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const [category, setCategory] = useState<Category | null>(null);
    const [loading, setLoading] = useState(true);
    const { showToast } = useToast();
    const navigate = useNavigate();

    // Modals
    const [courseModalOpen, setCourseModalOpen] = useState(false);
    const [editingCourse, setEditingCourse] = useState<Course | null>(null);

    const [sectionModalOpen, setSectionModalOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
    const [editingSection, setEditingSection] = useState<Section | null>(null);

    const [categoryModalOpen, setCategoryModalOpen] = useState(false);

    useEffect(() => {
        fetchCategoryDetails();
    }, [categoryId]);

    const fetchCategoryDetails = async () => {
        try {
            const res = await api.get(`/api/course/category/${categoryId}`);
            setCategory(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching category details", err);
            setLoading(false);
        }
    };

    // Course Handlers
    const handleOpenCourseModal = (course: Course | null = null) => {
        setEditingCourse(course);
        setCourseModalOpen(true);
    };

    const handleSaveCourse = async (data: any) => {
        try {
            if (editingCourse) {
                await api.put(`/api/course/course/${editingCourse._id}`, data);
            } else {
                await api.post('/api/course/course', {
                    ...data,
                    categoryId: categoryId
                });
            }
            fetchCategoryDetails();
        } catch (err) {
            console.error("Error saving course", err);
        }
    };

    const handleAiGenerate = async (data: { topic: string; description: string; instructions: string; modelChoose: string }) => {
        try {
            const res = await api.post('/api/course/ai-generate', {
                ...data,
                categoryId: categoryId
            });
            showToast("Cours généré avec succès", "success");
            if (res.data.courseId) {
                navigate(`/course/${res.data.courseId}`);
            }
        } catch (err: any) {
            console.error("Error generating course with AI", err);
            if (err.response && err.response.status === 429) {
                showToast("Quota OpenAI dépassé. Veuillez réessayer plus tard.", "error");
            } else {
                showToast("Erreur lors de la génération du cours", "error");
            }
            throw err;
        }
    };

    // Section Handlers
    const handleOpenSectionModal = (courseId: string, section: Section | null = null) => {
        setSelectedCourseId(courseId);
        setEditingSection(section);
        setSectionModalOpen(true);
    };

    const handleSaveSection = async (data: any) => {
        try {
            if (editingSection) {
                await api.put(`/api/course/section/${editingSection._id}`, data);
            } else {
                await api.post('/api/course/section', {
                    ...data,
                    courseId: selectedCourseId
                });
            }
            fetchCategoryDetails();
        } catch (err) {
            console.error("Error saving section", err);
        }
    };

    const handleSaveCategory = async (data: any) => {
        try {
            await api.post('/api/course/category', {
                ...data,
                parentId: categoryId
            });
            fetchCategoryDetails();
        } catch (err) {
            console.error("Error saving subcategory", err);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10, ...designTokens.mainBackground }}><CircularProgress /></Box>;
    if (!category) return <Typography>Category not found</Typography>;

    return (
        <Box sx={{ ...designTokens.mainBackground, pb: 8 }}>
            {/* Hero Header */}
            <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        ...designTokens.heroHeader,
                        p: { xs: 3, md: 6 },
                        borderRadius: 6,
                    }}
                >
                    {/* Decorative elements */}
                    <Box sx={{ position: 'absolute', top: -100, right: -100, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(60px)' }} />
                    <Box sx={{ position: 'absolute', bottom: -50, left: 200, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', filter: 'blur(40px)' }} />

                    <Container maxWidth="lg">
                        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 4, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.4)' } }}>
                            <Link
                                component={RouterLink}
                                to="/parcours"
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    fontFamily: 'Quicksand, sans-serif',
                                    '&:hover': { color: '#fff' }
                                }}
                            >
                                <ArrowBackIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                                Gestion des Parcours
                            </Link>
                            <Typography sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Quicksand, sans-serif' }}>
                                {category.title}
                            </Typography>
                        </Breadcrumbs>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    width: 70,
                                    height: 70,
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                                }}>
                                    <FolderIcon sx={{ fontSize: 35 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontFamily: 'Quicksand, sans-serif', mb: 0.5 }}>
                                        {category.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <Chip
                                            label={`${category.courses?.length || 0} cours`}
                                            sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}
                                        />
                                        <Chip
                                            label={`${category.categories?.length || 0} sous-catégories`}
                                            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', fontWeight: 600, fontFamily: 'Quicksand, sans-serif' }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<CreateNewFolderIcon />}
                                    onClick={() => setCategoryModalOpen(true)}
                                    sx={{
                                        bgcolor: 'rgba(255, 255, 255, 0.12)',
                                        backdropFilter: 'blur(10px)',
                                        color: '#fff',
                                        fontWeight: 700,
                                        textTransform: 'none',
                                        borderRadius: 3,
                                        px: 3,
                                        py: 1.2,
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        fontFamily: 'Quicksand, sans-serif',
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                                            transform: 'translateY(-3px)'
                                        },
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    Nouvelle Sous-catégorie
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenCourseModal(null)}
                                    sx={{
                                        ...designTokens.actionButton,
                                        background: '#fff',
                                        color: '#667eea',
                                        '&:hover': {
                                            background: '#f8fafc',
                                            transform: 'translateY(-3px)',
                                            boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
                                        },
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 3,
                                        fontWeight: 800,
                                        fontFamily: 'Quicksand, sans-serif',
                                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    Ajouter un Cours
                                </Button>
                            </Box>
                        </Box>
                    </Container>
                </Paper>
            </Box>

            <Container maxWidth="lg">
                {/* Sub-categories Section if any */}
                {category.categories && category.categories.length > 0 && (
                    <Box sx={{ mb: 6 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d3748', fontFamily: 'Quicksand, sans-serif', mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <LayersIcon sx={{ color: '#667eea' }} /> Sous-catégories
                        </Typography>
                        <Grid container spacing={3}>
                            {category.categories.map((subCat: Category) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={subCat._id}>
                                    <Card
                                        onClick={() => navigate(`/category/${subCat._id}`)}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 4,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            border: '1px solid rgba(102, 126, 234, 0.1)',
                                            '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 12px 30px rgba(102,126,234,0.1)' }
                                        }}
                                    >
                                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                                            <Avatar sx={{ bgcolor: 'rgba(102,126,234,0.08)', color: '#667eea' }}>
                                                <FolderIcon />
                                            </Avatar>
                                            <Typography sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif', color: '#2d3748' }}>
                                                {subCat.title}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SchoolIcon sx={{ color: '#667eea' }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d3748', fontFamily: 'Quicksand, sans-serif' }}>
                        Liste des Cours
                    </Typography>
                </Box>

                <Grid container spacing={4}>
                    {category.courses && category.courses.length > 0 ? (
                        category.courses.map((course: Course, index: number) => (
                            <Grid size={{ xs: 12, md: 6 }} key={course._id}>
                                <Fade in timeout={400 + index * 100}>
                                    <Card sx={{
                                        borderRadius: 5,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid rgba(0,0,0,0.04)',
                                        boxShadow: '0 3px 15px rgba(0,0,0,0.02)',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 15px 40px rgba(102,126,234,0.12)',
                                            borderColor: 'rgba(102,126,234,0.2)'
                                        }
                                    }}>
                                        <Box sx={{ height: 6, background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)' }} />
                                        <CardContent sx={{ p: 4 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', color: '#2d3748', lineHeight: 1.3 }}>
                                                    {course.title}
                                                </Typography>
                                                <Tooltip title="Aperçu apprenant">
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/learner/course/${course._id}`);
                                                        }}
                                                        sx={{ color: '#667eea', bgcolor: 'rgba(102,126,234,0.05)' }}
                                                    >
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>

                                            <Typography variant="body2" sx={{
                                                color: '#718096',
                                                fontFamily: 'Quicksand, sans-serif',
                                                fontWeight: 500,
                                                mb: 3,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                minHeight: 40
                                            }}>
                                                {course.description || "Aucune description disponible pour ce cours."}
                                            </Typography>

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Chip
                                                    label="Cours actif"
                                                    size="small"
                                                    sx={{ bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontWeight: 700, borderRadius: 2 }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    endIcon={<EditIcon fontSize="small" />}
                                                    onClick={() => navigate(`/course/${course._id}`)}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        borderRadius: 3,
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        px: 3,
                                                        fontFamily: 'Quicksand, sans-serif',
                                                        boxShadow: '0 4px 12px rgba(102,126,234,0.2)'
                                                    }}
                                                >
                                                    Édition complète
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            </Grid>
                        ))
                    ) : (
                        <Grid size={{ xs: 12 }}>
                            <Paper sx={{
                                p: 10,
                                textAlign: 'center',
                                borderRadius: 6,
                                border: '2px dashed rgba(102,126,234,0.2)',
                                bgcolor: 'rgba(255,255,255,0.5)'
                            }}>
                                <Avatar sx={{ bgcolor: 'rgba(102,126,234,0.1)', color: '#667eea', width: 80, height: 80, mx: 'auto', mb: 3 }}>
                                    <SchoolIcon sx={{ fontSize: 40 }} />
                                </Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d3748', fontFamily: 'Quicksand, sans-serif', mb: 1 }}>
                                    Aucun cours dans cette catégorie
                                </Typography>
                                <Typography sx={{ color: '#718096', fontWeight: 500, fontFamily: 'Quicksand, sans-serif', mb: 4 }}>
                                    Appuyez sur "Ajouter un cours" pour commencer.
                                </Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </Container>

            {/* Modals */}
            <CourseModal
                open={courseModalOpen}
                onClose={() => setCourseModalOpen(false)}
                onSave={handleSaveCourse}
                onAiGenerate={handleAiGenerate}
                initialData={editingCourse || undefined}
                isEditing={!!editingCourse}
            />

            <SectionModal
                open={sectionModalOpen}
                onClose={() => setSectionModalOpen(false)}
                onSave={handleSaveSection}
                initialData={editingSection || undefined}
                isEditing={!!editingSection}
            />

            <CategoryModal
                open={categoryModalOpen}
                onClose={() => setCategoryModalOpen(false)}
                onSave={handleSaveCategory}
            />
        </Box>
    );
};

export default CategoryDetails;
